"use client"

import { useEffect, useState } from "react"

const HISTORY = 7
const SAMPLE_MS = 70
const silence = () => Array<number>(HISTORY).fill(0)

/** Loudness of one analyser frame of byte time-domain samples, 0 to 1. */
export function micLevel(samples: Uint8Array) {
	let sum = 0
	for (const sample of samples) sum += ((sample - 128) / 128) ** 2
	return Math.min(1, Math.sqrt(sum / samples.length) * 5)
}

/** Each bar reads further back in the history, so the row moves like a wave. */
export function barScales(levels: number[]) {
	return [0, 2, 4, 6].map((back, i) => {
		const level = levels[levels.length - 1 - back] ?? 0
		const weight = i === 1 || i === 2 ? 1 : 0.75
		return 0.2 + 0.8 * Math.min(1, level * weight)
	})
}

/**
 * Live microphone loudness while `active`, newest last. The speech recogniser
 * exposes no levels, so this reads the mic itself; where it cannot, it stays silent.
 */
export function useMicLevels(active: boolean) {
	const [levels, setLevels] = useState(silence)

	useEffect(() => {
		if (!active || !navigator.mediaDevices?.getUserMedia) return
		let released = false
		let stream: MediaStream | undefined
		let context: AudioContext | undefined
		let timer: ReturnType<typeof setInterval> | undefined

		const release = () => {
			released = true
			clearInterval(timer)
			stream?.getTracks().forEach((track) => track.stop())
			void context?.close().catch(() => undefined)
		}

		navigator.mediaDevices
			.getUserMedia({ audio: true })
			.then((granted) => {
				stream = granted
				if (released) {
					release()
					return
				}
				context = new AudioContext()
				void context.resume().catch(() => undefined)
				const analyser = context.createAnalyser()
				analyser.fftSize = 256
				context.createMediaStreamSource(granted).connect(analyser)
				const samples = new Uint8Array(analyser.fftSize)
				timer = setInterval(() => {
					analyser.getByteTimeDomainData(samples)
					const level = micLevel(samples)
					setLevels((previous) => [...previous.slice(1), level])
				}, SAMPLE_MS)
			})
			.catch(release)

		return () => {
			release()
			setLevels(silence)
		}
	}, [active])

	return levels
}
